import type { EntitySnapshot, PlayerSnapshot, SimStateSnapshot } from '@md/shared';
import type { Haptics } from './haptics';
import type { SfxPlayer } from './sfx';

/**
 * Pont état de simulation → sons/vibrations (phase 5).
 *
 * RÈGLE ABSOLUE DU PROJET : la simulation reste pure et déterministe. Ce
 * module ne fait qu'OBSERVER des instantanés déjà produits par le moteur —
 * il ne les modifie jamais et n'a aucune influence sur `tick()`. Il compare
 * chaque nouvel instantané au précédent et en déduit les effets à jouer.
 */

/** Vibration courte sur `attract` (ms). */
const ATTRACT_VIBRATION_MS = 20;
/** Motif de vibration plus marqué sur `repel` (ms, vibration/pause/vibration…). */
const REPEL_VIBRATION_PATTERN: readonly number[] = [30, 40, 60];
/** Vibration sur `death` (ms). */
const DEATH_VIBRATION_MS = 40;
/** Motif de vibration de victoire (ms). */
const VICTORY_VIBRATION_PATTERN: readonly number[] = [50, 50, 50, 50, 150];

/** Intensité du bourdonnement quand les deux robots sont liés. */
const HUM_INTENSITY_LINKED = 1;
/** Intensité du bourdonnement quand un seul champ est actif. */
const HUM_INTENSITY_SINGLE = 0.5;

export interface AudioReactorOptions {
  readonly sfx: SfxPlayer;
  readonly haptics: Haptics;
}

export interface AudioReactor {
  /** Appelé à chaque frame avec l'instantané courant ; compare avec le précédent et déclenche les effets. */
  observe(state: SimStateSnapshot | null): void;
  /** Oublie l'état précédent (changement de niveau) : aucun son parasite au chargement. */
  reset(): void;
}

/** État d'ouverture d'une entité `door`, quel que soit le nom du drapeau utilisé par le module de jeu. */
function doorState(entity: EntitySnapshot): number {
  return entity.flags.open ?? entity.flags.active ?? 0;
}

/** Compare les deux robots d'un instantané à l'instantané précédent et joue les effets par-joueur. */
function evaluatePlayers(
  previous: SimStateSnapshot,
  current: SimStateSnapshot,
  sfx: SfxPlayer,
  haptics: Haptics,
): void {
  const indices: readonly [0, 1] = [0, 1];
  for (const index of indices) {
    const before: PlayerSnapshot = previous.players[index];
    const after: PlayerSnapshot = current.players[index];

    if (before.onGround && !after.onGround && after.vy < 0) {
      sfx.play('jump');
    }
    if (!before.onGround && after.onGround) {
      sfx.play('land');
    }
    if (before.fieldActive !== after.fieldActive) {
      sfx.play(after.fieldActive ? 'field-on' : 'field-off');
    }

    const wasFullyLinked = before.flags.linked === 1 && before.flags.repelling === 1;
    const isFullyLinked = after.flags.linked === 1 && after.flags.repelling === 1;

    if (after.flags.linked === 1 && before.flags.linked !== 1 && after.flags.repelling !== 1) {
      sfx.play('attract');
      haptics.vibrate(ATTRACT_VIBRATION_MS);
    }
    if (isFullyLinked && !wasFullyLinked) {
      sfx.play('repel');
      haptics.vibrate(REPEL_VIBRATION_PATTERN);
    }
  }
}

/** Compare les entités (boutons, leviers, portes…) entre deux instantanés. */
function evaluateEntities(
  previous: SimStateSnapshot,
  current: SimStateSnapshot,
  sfx: SfxPlayer,
): void {
  const previousById = new Map(previous.entities.map((entity) => [entity.id, entity]));
  for (const entity of current.entities) {
    const before = previousById.get(entity.id);
    if (!before) {
      continue;
    }
    if (entity.type === 'button' || entity.type === 'lever') {
      if (before.flags.active !== entity.flags.active) {
        sfx.play('switch');
      }
    }
    if (entity.type === 'door' && doorState(before) !== doorState(entity)) {
      sfx.play('door');
    }
  }
}

/** Compare les compteurs globaux (boulons, morts, victoire) entre deux instantanés. */
function evaluateGlobals(
  previous: SimStateSnapshot,
  current: SimStateSnapshot,
  sfx: SfxPlayer,
  haptics: Haptics,
): void {
  if (current.bolts.collected > previous.bolts.collected) {
    sfx.play('bolt');
  }
  if (current.deaths > previous.deaths) {
    sfx.play('death');
    haptics.vibrate(DEATH_VIBRATION_MS);
  }
  if (current.won && !previous.won) {
    sfx.play('victory');
    haptics.vibrate(VICTORY_VIBRATION_PATTERN);
  }
}

/**
 * Bourdonnement magnétique continu : 1 quand les deux robots sont liés, 0.5
 * quand un seul champ est actif, coupé sinon.
 */
function updateHum(state: SimStateSnapshot, sfx: SfxPlayer): void {
  const linkedBoth = state.players[0].flags.linked === 1 && state.players[1].flags.linked === 1;
  if (linkedBoth) {
    sfx.setHum(HUM_INTENSITY_LINKED);
    return;
  }
  const activeFields = state.players.filter((player) => player.fieldActive).length;
  if (activeFields === 1) {
    sfx.setHum(HUM_INTENSITY_SINGLE);
    return;
  }
  sfx.setHum(null);
}

/**
 * Construit le pont état de simulation → sons/vibrations.
 *
 * `observe()` doit être appelé à chaque frame (ou chaque nouvel instantané
 * reçu du réseau) ; `reset()` doit l'être explicitement au chargement d'un
 * niveau, pour ne jamais confondre le premier instantané du nouveau niveau
 * avec une transition du précédent.
 */
export function createAudioReactor(options: AudioReactorOptions): AudioReactor {
  const { sfx, haptics } = options;
  let previous: SimStateSnapshot | null = null;

  return {
    observe(state: SimStateSnapshot | null): void {
      if (!state) {
        previous = null;
        sfx.setHum(null);
        return;
      }
      if (previous && state.tick < previous.tick) {
        // Reset de niveau (mort, restart…) : on oublie le passé sans jouer de son.
        previous = state;
        sfx.setHum(null);
        return;
      }
      if (previous) {
        evaluatePlayers(previous, state, sfx, haptics);
        evaluateEntities(previous, state, sfx);
        evaluateGlobals(previous, state, sfx, haptics);
      }
      updateHum(state, sfx);
      previous = state;
    },

    reset(): void {
      previous = null;
      sfx.setHum(null);
    },
  };
}
